/**
 * Where each step sits on the canvas, and the path each arrow takes.
 *
 * LAID OUT, NEVER DRAGGED. Authors are not asked to arrange boxes: every edit
 * re-runs dagre top to bottom, so the first step is at the top, a branch fans
 * out beneath the step it leaves, and the picture always reads in the order a
 * call walks it. Each arrow's label is given space of its own by dagre, so two
 * labels never sit on top of each other.
 *
 * Plain functions, so they can be tested without React.
 */

import dagre from '@dagrejs/dagre';

/** The box size, in px. Canvas.tsx draws it as `w-66 h-26`; keep the two equal. */
export const STEP_WIDTH = 264;
export const STEP_HEIGHT = 104;

export interface Point {
  x: number;
  y: number;
}

export interface LayoutEdge {
  id: string;
  source: string;
  target: string;
  label: string;
}

export interface LaidOutEdge extends LayoutEdge {
  /** An SVG path through dagre's points. */
  path: string;
  labelAt: Point;
}

export interface Layout {
  /** Top-left corner of each step, as React Flow positions a node. */
  positions: Map<string, Point>;
  edges: LaidOutEdge[];
}

/** Rough width of a label chip plus its add button, for dagre to leave room. */
function labelWidth(label: string): number {
  return label ? Math.min(label.length * 6.6 + 56, 280) : 32;
}

export function layoutGraph(nodeIds: readonly string[], edges: readonly LayoutEdge[]): Layout {
  const g = new dagre.graphlib.Graph();
  g.setGraph({ rankdir: 'TB', nodesep: 48, ranksep: 28, edgesep: 24, marginx: 24, marginy: 24 });
  g.setDefaultEdgeLabel(() => ({}));
  for (const id of nodeIds) g.setNode(id, { width: STEP_WIDTH, height: STEP_HEIGHT });
  for (const e of edges) {
    g.setEdge(e.source, e.target, { width: labelWidth(e.label), height: 28, labelpos: 'c' });
  }
  dagre.layout(g);

  const positions = new Map<string, Point>();
  for (const id of nodeIds) {
    const n = g.node(id);
    positions.set(id, { x: n.x - STEP_WIDTH / 2, y: n.y - STEP_HEIGHT / 2 });
  }
  const laidOut = edges.map((e): LaidOutEdge => {
    const ge = g.edge(e.source, e.target);
    const points = ge.points ?? [];
    return { ...e, path: smoothPath(points), labelAt: { x: ge.x ?? 0, y: ge.y ?? 0 } };
  });
  return { positions, edges: laidOut };
}

/** A smooth curve through the points (a B-spline, as d3's `curveBasis` draws). */
export function smoothPath(points: readonly Point[]): string {
  if (points.length === 0) return '';
  const first = points[0]!;
  const last = points[points.length - 1]!;
  if (points.length < 3) return `M${first.x},${first.y}L${last.x},${last.y}`;
  let d = `M${first.x},${first.y}`;
  let x0 = first.x;
  let y0 = first.y;
  let x1 = first.x;
  let y1 = first.y;
  const curve = (x: number, y: number): void => {
    d +=
      `C${(2 * x0 + x1) / 3},${(2 * y0 + y1) / 3},${(x0 + 2 * x1) / 3},${(y0 + 2 * y1) / 3},` +
      `${(x0 + 4 * x1 + x) / 6},${(y0 + 4 * y1 + y) / 6}`;
  };
  points.forEach((p, i) => {
    if (i === 2) {
      d += `L${(5 * x0 + x1) / 6},${(5 * y0 + y1) / 6}`;
      curve(p.x, p.y);
    } else if (i > 2) {
      curve(p.x, p.y);
    }
    x0 = x1;
    y0 = y1;
    x1 = p.x;
    y1 = p.y;
  });
  curve(x1, y1);
  return `${d}L${x1},${y1}`;
}
